import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { toast } from 'react-toastify';
import { InferType } from 'yup';

import {
  useActivityLogQuery,
  useAddActivityLogMutation,
  useEditActivityLogMutation,
} from '@common/cache';
import { formatApiError } from '@common/errors/error-formatting';
import { addActivityLogSchema } from '../schemas';
import dayjs from 'dayjs';

type IFormShape = InferType<typeof addActivityLogSchema>;

const initialValues: Partial<IFormShape> = {
  practice_id: '',
  provider_id: '',
  building_id: '',
  description: '',
  timestamp: new Date().toISOString(),
  title: '',
};

export const useAddActivityLog = (
  activity_id: string,
  variant: 'create' | 'edit',
) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isSaveChangesModalOpen, setIsSaveChangesModalOpen] = useState(false);
  const { mutateAsync: addActivityLogMutation, isPending: addIsPending } =
    useAddActivityLogMutation();
  const { mutateAsync: editActivityLogMutation, isPending: editIsPending } =
    useEditActivityLogMutation(activity_id);

  const formMethods = useForm<IFormShape>({
    mode: 'onChange',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: yupResolver<IFormShape, any, any>(addActivityLogSchema),
    defaultValues: initialValues,
  });
  //passing the isOpen here to ensure that the fetches take place only when the modal is open
  const { data } = useActivityLogQuery(activity_id, isOpen);

  const {
    handleSubmit,
    formState: { isSubmitting, isValid, isDirty },
    reset,
  } = formMethods;

  useEffect(() => {
    if (isOpen) {
      if (variant === 'edit' && data) {
        reset({
          title: data.title,
          timestamp: data.timestamp,
          description: data.description,
          recorded_minutes: data.recorded_minutes,
          provider_id: data.provider_id,
          practice_id: data.practice_id,
          building_id: data.building_id,
        });
      } else {
        reset({ ...initialValues, timestamp: new Date().toISOString() });
      }
    }
  }, [data, isOpen]);

  const handleClose = () => {
    if (isDirty) {
      setIsSaveChangesModalOpen(true);
    } else {
      setIsOpen(false);
    }
  };

  const handleOpen = () => {
    setIsOpen(true);
  };

  const submitHandler = async (values: IFormShape) => {
    const action =
      variant === 'edit' ? editActivityLogMutation : addActivityLogMutation;
    const successMessage =
      variant === 'create'
        ? 'You logged a new activity!'
        : 'Activity edited successfully';
    try {
      const isoTime = (
        values.timestamp.length === 10
          ? dayjs(`${values.timestamp}T${dayjs().format('HH:mm:ss')}`)
          : dayjs(values.timestamp)
      )
        .toDate()
        .toISOString();
      await action({
        title: values?.title || '',
        timestamp: isoTime,
        description: values?.description,
        recorded_minutes: values?.recorded_minutes || 0,
        ...(values.provider_id && { provider_id: values.provider_id }),
        ...(values.practice_id && { practice_id: values.practice_id }),
        ...(values.building_id && { building_id: values.building_id }),
      });
      setIsSaveChangesModalOpen(false);
      setIsOpen(false);
      reset({ ...initialValues, timestamp: new Date().toISOString() });
      toast.success(successMessage);
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const onSubmit = handleSubmit(submitHandler);

  const handleCloseSaveChangesModal = () => {
    setIsSaveChangesModalOpen(false);
  };

  const handleDiscardChanges = () => {
    setIsSaveChangesModalOpen(false);
    setIsOpen(false);
    reset({ ...initialValues, timestamp: new Date().toISOString() });
  };

  const handleSaveChanges = async () => {
    await onSubmit();
  };

  return {
    isOpen,
    handleClose,
    handleOpen,
    onSubmit,
    isSubmitting,
    isValid,
    formMethods,
    isSaveChangesModalOpen,
    handleCloseSaveChangesModal,
    handleSaveChanges,
    handleDiscardChanges,
    isEditSubmitting: addIsPending || editIsPending,
  };
};
